import React, { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { 
  Calendar, 
  Users, 
  Loader2,
  AlertCircle,
  Building2,
  FileText,
  DollarSign,
  ExternalLink
} from 'lucide-react';
import api from '../lib/api';
import PublicLayout from '@shared/ui/PublicLayout';
import Chip from '../components/Chip';
import CalendarEventDisplay from '../components/shared/CalendarEventDisplay';
import AISummarySection from '../components/billDetail/AISummarySection';
import { formatDate } from '../utils/dateUtils';

interface Sponsor {
  sponsor_type: string;
  legislator_id: string;
  name: string;
  party: string;
  chamber: string;
}

interface Document {
  id: number;
  name: string;
  url: string;
  type: string;
}

interface Lobbyist {
  id: number;
  lobbyist_id: string;
  lobbyist_name: string;
  lobbyist_firm_name: string;
  client_name: string;
  position: string;
}

interface Committee {
  name: string;
  chamber: string;
}

interface AiSummary {
  short_summary: string | null;
  long_summary: string | null;
  pro_position_summary: string | null;
  con_position_summary: string | null;
}

interface FiscalNote {
  id: number;
  url: string;
  version: string;
  date: string;
}

interface NextCalendarEvent {
  calendar_id: number;
  reading_date: string;
  reading_time: string | null;
  reading_title: string;
  room_number: string | null;
  chamber: string;
  upon_adjournment: boolean;
}

interface BillAmendment {
  amendment_number: string;
  committee_hearing: string;
  status: string;
  url: string;
  date: string;
}

interface BillData {
  id: number;
  number: string;
  title: string;
  concerning: string;
  summary: string;
  session: string;
  status: string;
  introducedDate: string;
  chamber: string;
  sponsors: Sponsor[];
  subjects: string[];
  committees: Committee[];
  documents: Document[];
  amendments: BillAmendment[];
  /** Lobbyists visible to this caller. The server truncates to the first 5
   *  for anonymous callers and returns the full list for authenticated ones. */
  lobbyists: Lobbyist[];
  /** Total number of lobbyists associated with the bill, regardless of whether
   *  the response was truncated for visibility reasons. */
  lobbyistTotalCount?: number;
  /** True when `lobbyists` was truncated (anonymous caller with > 5 lobbyists). */
  lobbyistsTruncated?: boolean;
  /** Echoed by the server so the client can render auth-aware UI without
   *  trusting localStorage (which can hold stale/expired tokens). */
  viewerIsAuthenticated?: boolean;
  fiscalNotes: FiscalNote[];
  aiSummary: AiSummary | null;
  nextCalendarEvent?: NextCalendarEvent | null;
}

/**
 * PublicBillDetailPage - Public view of bill details
 * 
 * Shows limited bill information without requiring authentication.
 * Encourages users to sign up for more details.
 */
const PublicBillDetailPage: React.FC = () => {
  const { billNumber } = useParams<{ billNumber: string }>();
  const [searchParams] = useSearchParams();
  const [bill, setBill] = useState<BillData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const amendmentsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchBill = async () => {
      if (!billNumber) return;
      
      try {
        setLoading(true);
        setError(null);
        // Use the configured api client (not raw axios) so any present auth
        // token is forwarded. The server uses optionalAuthMiddleware on this
        // endpoint to upgrade the response (full lobbyist list, etc.) when
        // the caller is authenticated.
        const response = await api.get(`/bills/public/${billNumber}`);
        if (response.data.success) {
          setBill(response.data.data);
        } else {
          setError(response.data.error || 'Failed to load bill');
        }
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load bill information');
      } finally {
        setLoading(false);
      }
    };

    fetchBill();
  }, [billNumber]);

  useEffect(() => {
    if (!loading && bill && searchParams.get('tab') === 'amendments' && amendmentsRef.current) {
      amendmentsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, bill, searchParams]);


  // Get party abbreviation
  const getPartyAbbr = (party: string) => {
    if (!party) return '';
    const p = party.toLowerCase();
    if (p.includes('democrat')) return 'D';
    if (p.includes('republican')) return 'R';
    return 'I';
  };

  // Get primary document (bill text)
  const getPrimaryDocument = () => {
    if (!bill?.documents?.length) return null;
    // Look for the main bill text
    return bill.documents.find(d => 
      d.type?.toLowerCase().includes('introduced') || 
      d.name?.toLowerCase().includes('introduced')
    ) || bill.documents[0];
  };

  // Loading state
  if (loading) {
    return (
      <PublicLayout showFooter={false}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          <span className="ml-3 text-text-secondary dark:text-text-dark-secondary">
            Loading bill...
          </span>
        </div>
      </PublicLayout>
    );
  }

  // Error state
  if (error || !bill) {
    return (
      <PublicLayout showFooter={true}>
        <div className="text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-2">
            Bill Not Found
          </h2>
          <p className="text-text-secondary dark:text-text-dark-secondary mb-6">
            {error || `The bill "${billNumber}" could not be found.`}
          </p>
          <Link 
            to="/"
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            Go to Home
          </Link>
        </div>
      </PublicLayout>
    );
  }

  const primaryDocument = getPrimaryDocument();

  const serverGateKnown =
    typeof bill.viewerIsAuthenticated === 'boolean' ||
    typeof bill.lobbyistsTruncated === 'boolean' ||
    typeof bill.lobbyistTotalCount === 'number';
  const viewerIsAuthenticated = serverGateKnown
    ? Boolean(bill.viewerIsAuthenticated)
    : false;

  return (
    <PublicLayout
      organizationName="Colorado Legislature"
      showFooter={true}
      viewerIsAuthenticated={bill.viewerIsAuthenticated}
    >
      {/* Hero Section */}
      <div className="card p-4 sm:p-6 mb-3 sm:mb-4 lg:mb-6">
        {/* Bill Number and Subject Tags */}
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <span className="text-text-secondary dark:text-text-dark-secondary font-mono text-sm">{bill.number}</span>
          {bill.subjects?.slice(0, 2).map((subject, idx) => (
            <Chip
              key={idx}
              label={subject}
              variant="primary"
              size="xs"
            />
          ))}
        </div>

        {/* Bill Title */}
        <h1 className="text-2xl sm:text-3xl font-bold text-text-primary dark:text-text-dark-primary mb-3">
          {bill.title || bill.concerning || 'Untitled Bill'}
        </h1>

        {/* Description */}
        {bill.concerning && (
          <p className="text-text-secondary dark:text-text-dark-secondary text-sm mb-4">
            {bill.concerning}
          </p>
        )}

        {/* Meta Info - two-column: left = dates/sponsors, right = document links */}
        <div className="flex flex-col sm:flex-row gap-4 text-sm">
          <div className="flex-1 space-y-2">
            {/* Introduced Date */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
              <span className="text-text-secondary dark:text-text-dark-secondary">Introduced</span>
              <span className="font-medium text-text-primary dark:text-text-dark-primary">{formatDate(bill.introducedDate)}</span>
            </div>

            {/* Next Calendar Event */}
            {bill.nextCalendarEvent && (
              <CalendarEventDisplay
                events={[bill.nextCalendarEvent]}
                billNumber={bill.number}
                billTitle={bill.title || bill.concerning}
                variant="inline"
                context="public"
                showIcon={false}
                showLabel={true}
              />
            )}

            {/* Sponsors */}
            {bill.sponsors?.length > 0 && (
              <div className="flex items-start gap-2">
                <Users className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-text-secondary dark:text-text-dark-secondary">Sponsors: </span>
                  {bill.sponsors.map((sponsor, idx) => (
                    <span key={idx}>
                      <Link 
                        to={`/public/legislators/${sponsor.legislator_id}`}
                        className="font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
                      >
                        {sponsor.name} ({getPartyAbbr(sponsor.party)})
                      </Link>
                      {idx < bill.sponsors.length - 1 && <span className="text-text-tertiary dark:text-text-dark-tertiary">, </span>}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Document Links */}
          {(primaryDocument || bill.fiscalNotes?.length > 0) && (
            <div className="space-y-2 sm:text-right">
              {primaryDocument && (
                <a
                  href={primaryDocument.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 sm:justify-end text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                >
                  <FileText className="w-4 h-4" />
                  Full Bill Text
                </a>
              )}
              {bill.fiscalNotes?.length > 0 && (
                <a
                  href={bill.fiscalNotes[0].url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 sm:justify-end text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                >
                  <DollarSign className="w-4 h-4" />
                  Fiscal Note
                </a>
              )}
            </div>
          )}
        </div>

        {/* Status and Committee - 2 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-border-light dark:border-border-dark">
          {/* Status */}
          <div>
            <h3 className="text-xs font-medium text-text-tertiary dark:text-text-dark-tertiary uppercase tracking-wide mb-1">
              Status
            </h3>
            <p className="text-text-primary dark:text-text-dark-primary text-sm">
              {bill.status || 'Unknown'}
            </p>
          </div>

          {/* Committee */}
          {bill.committees?.length > 0 && (
            <div>
              <h3 className="text-xs font-medium text-text-tertiary dark:text-text-dark-tertiary uppercase tracking-wide mb-1">
                Committee
              </h3>
              <p className="text-text-primary dark:text-text-dark-primary text-sm">
                {bill.committees[0].name}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* AI Summary Section */}
      {bill.aiSummary && (
        <AISummarySection aiSummary={bill.aiSummary} className="mb-3 sm:mb-4 lg:mb-6" />
      )}

      {/* Bill Summary */}
      {bill.summary && (
        <div className="card mb-3 sm:mb-4 lg:mb-6">
          <h3 className="text-xl font-semibold text-text-primary dark:text-text-dark-primary mb-4 pb-2 border-b border-border-light dark:border-border-dark">
            Bill Summary
          </h3>
          <p className="text-text-secondary dark:text-text-dark-secondary leading-relaxed">{bill.summary}</p>
        </div>
      )}

      {/* Amendments Section */}
      {bill.amendments?.length > 0 && (
        <div ref={amendmentsRef} className="card p-4 mb-3 sm:mb-4 lg:mb-6">
          <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-4">
            Amendments ({bill.amendments.length})
          </h2>
          <div className="space-y-3">
            {bill.amendments.map((amendment, index) => (
              <div key={index} className="p-3 bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-text-primary dark:text-text-dark-primary">
                    Amendment {amendment.amendment_number}
                  </span>
                  {amendment.status && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      amendment.status.toLowerCase().includes('passed') || amendment.status.toLowerCase().includes('adopted')
                        ? 'bg-success-100 text-success-800 dark:bg-success-900/20 dark:text-success-300'
                        : amendment.status.toLowerCase().includes('failed') || amendment.status.toLowerCase().includes('rejected')
                        ? 'bg-danger-100 text-danger-800 dark:bg-danger-900/20 dark:text-danger-300'
                        : 'bg-background-tertiary text-text-primary dark:bg-background-dark-tertiary dark:text-text-dark-primary'
                    }`}>
                      {amendment.status}
                    </span>
                  )}
                </div>
                {amendment.committee_hearing && (
                  <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-1">
                    {amendment.committee_hearing}
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                    {amendment.date ? formatDate(amendment.date) : 'Date not available'}
                  </span>
                  {amendment.url && (
                    <a
                      href={amendment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
                    >
                      View Amendment <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active Lobbyists Section
       *
       * The server controls visibility via optionalAuthMiddleware on
       * GET /api/bills/public/:billNumber:
       *   - Anonymous caller → first 5 lobbyists, lobbyistsTruncated=true
       *     when there are more, lobbyistTotalCount = full count,
       *     viewerIsAuthenticated=false.
       *   - Authenticated caller → full list, lobbyistsTruncated=false,
       *     viewerIsAuthenticated=true.
       *
       * Defensive fallback: if the server didn't return any of the
       * `viewerIsAuthenticated` / `lobbyistsTruncated` / `lobbyistTotalCount`
       * fields (e.g. a backend deploy lagging behind the frontend), assume
       * anonymous and truncate client-side. This guarantees the gated view
       * during mid-rollout — we never accidentally leak the full list. */}
      {(() => {
        const PUBLIC_LIMIT = 5;

        const visibleLobbyists = viewerIsAuthenticated
          ? bill.lobbyists
          : bill.lobbyists.slice(0, PUBLIC_LIMIT);

        const totalCount = bill.lobbyistTotalCount ?? bill.lobbyists.length;
        const lobbyistsTruncated = !viewerIsAuthenticated && totalCount > visibleLobbyists.length;

        if (totalCount === 0) return null;

        return (
          <div className="card p-4 mb-3 sm:mb-4 lg:mb-6">
            <div className="flex items-baseline justify-between gap-3 mb-4">
              <h2 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary">
                Active Lobbyists
              </h2>
              <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                {totalCount} total
              </span>
            </div>
            <div className="space-y-3">
              {visibleLobbyists.map((lobbyist, idx) => (
                <div key={lobbyist.id ?? idx} className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-background-tertiary dark:bg-background-dark-tertiary rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-text-tertiary dark:text-text-dark-tertiary" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-text-primary dark:text-text-dark-primary truncate">
                      {lobbyist.client_name || lobbyist.lobbyist_firm_name || lobbyist.lobbyist_name}
                    </p>
                    {viewerIsAuthenticated &&
                      (lobbyist.lobbyist_name || lobbyist.lobbyist_firm_name) && (
                        <p className="text-xs text-text-secondary dark:text-text-dark-secondary truncate">
                          {[lobbyist.lobbyist_name, lobbyist.lobbyist_firm_name]
                            .filter(Boolean)
                            .filter((v) => v !== lobbyist.client_name)
                            .join(' · ')}
                        </p>
                      )}
                    {lobbyist.position && (
                      <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                        Position: {lobbyist.position}
                      </p>
                    )}
                  </div>
                </div>
              ))}
              {lobbyistsTruncated && (
                <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary pl-13">
                  +{totalCount - visibleLobbyists.length} more lobbyists
                </p>
              )}
            </div>
            {lobbyistsTruncated && (
              <div className="mt-4 pt-4 border-t border-border-light dark:border-border-dark">
                <Link
                  to={`/login?returnUrl=${encodeURIComponent(`/legislation/${billNumber}?tab=lobbyists`)}`}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline font-medium"
                >
                  Sign in to view full lobbyist details
                </Link>
              </div>
            )}
          </div>
        );
      })()}

    </PublicLayout>
  );
};

export default PublicBillDetailPage;
